import { AppStateEnum } from '../../../../../main/types/app-state.types.ts';

import { CharacterBubbleAnimation } from '../../../../constants/character-bubble-animations.ts';
import { selectCurrentLessonTaskStepKey } from '../../../../selectors/current-lesson-task-step-key.selector.ts';
import { selectCurrentLessonTaskStep } from '../../../../selectors/current-lesson-task-step.selector.ts';
import { triggerCharacterBubbleAnimationThunk } from '../../../../slices/app-state/thunks/trigger-character-bubble-animation.thunk.ts';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk.ts';
import { setBallIsVisible } from '../../../../slices/ball/ball.slice.ts';

import { BaseAppStateChangeHandlerUtil } from '../base-app-state-change.handler.ts';

/**
 * Base handler for all "ask to tap" interactions
 * Handles the common state flow while allowing specialized implementations
 * to customize the tap interaction and validation logic
 */
export abstract class BaseAskToTapStateChangeHandlerUtil extends BaseAppStateChangeHandlerUtil {
  protected lastTappedItem: string | null = null;

  protected abstract getHandlerName(): string;

  protected override async onReadyToStart(): Promise<void> {
    await super.onReadyToStart();
    console.log(`${this.getHandlerName()} onReadyToStart`);
    this.incorrectAttemptsUtil.handleStartStep();
    await this.handleStartStep();
  }

  protected override async onRetry(): Promise<void> {
    await super.onRetry();
    console.log(`${this.getHandlerName()} onRetry`);
    await this.handleStartStep();
  }

  protected override async onAwaitingTap(): Promise<void> {
    console.log(`${this.getHandlerName()} onAwaitingTap`);
    const dispatch = await this.reduxStoreUtil.getDispatch();
    const state = await this.reduxStoreUtil.getRootState();
    const { step } = selectCurrentLessonTaskStep(state);

    this.resetBallPosition();

    // Ensure step has a target
    if (!step.target) {
      this.handleError(new Error('Invalid step - missing target'));
      return;
    }

    try {
      // Wait for tap and validate - these methods are implemented by specialized subclasses
      this.lastTappedItem = await this.waitForTapInteraction();
      const isCorrect = await this.validateTap(this.lastTappedItem);

      // Transition based on validation result
      await dispatch(
        updateAppStateThunk({
          state: isCorrect ? AppStateEnum.Correct : AppStateEnum.Incorrect,
          blockAnimation: true,
        })
      ).unwrap();
    } catch (error) {
      this.handleError(error as Error);
      await this.handleRetryAfterError();
    }
  }

  protected override async onCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onCorrect`);
    await this.handleCorrect({
      assuming: false,
      answer: this.lastTappedItem ?? 'No response',
    });
  }

  protected override async onAssumeCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onAssumeCorrect`);
    await this.handleCorrect({
      assuming: true,
      answer: this.lastTappedItem ?? 'No response',
    });
  }

  protected override async onIncorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onIncorrect`);
    await this.handleIncorrect(this.lastTappedItem ?? 'No response');
  }

  protected override async onReadyForNextStep(): Promise<void> {
    await super.onReadyForNextStep();
    console.log(`${this.getHandlerName()} onReadyForNextStep`);
    const dispatch = await this.reduxStoreUtil.getDispatch();
    const state = await this.reduxStoreUtil.getRootState();

    // Check if this is the final step
    const stepKey = selectCurrentLessonTaskStepKey(state);
    if (this.lessonsUtil.isFinalStep(stepKey)) {
      // For final step, show character animation and play audio
      dispatch(triggerCharacterBubbleAnimationThunk(CharacterBubbleAnimation.SHAKE));

      await this.audioPlayerUtil.speakStep();
      await this.audioPlayerUtil.waitSpeakCompletion();

      dispatch(setBallIsVisible(true));
    } else {
      // For non-final steps, move to the next step
      await dispatch(updateAppStateThunk({ state: AppStateEnum.MoveToNextStep })).unwrap();
    }
  }

  private async handleStartStep(): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();

    // Reset state
    this.lastTappedItem = null;
    this.resetBallPosition();

    // Allow specialized setup
    await this.prepareForTapInteraction();

    // Play instruction audio
    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.waitSpeakCompletion();

    // Transition to awaiting tap state
    await dispatch(updateAppStateThunk({ state: AppStateEnum.AwaitingTap, blockAnimation: true })).unwrap();
  }

  /**
   * Prepares the UI for the tap interaction
   * To be implemented by subclasses if needed (optional)
   */
  protected async prepareForTapInteraction(): Promise<void> {
    // Default implementation does nothing
  }

  /**
   * Waits for a tap interaction and returns data representing the tap
   * Must be implemented by subclasses
   */
  protected abstract waitForTapInteraction(): Promise<string>;

  /**
   * Validates the tap and determines if it was correct
   * Must be implemented by subclasses
   */
  protected abstract validateTap(tappedItem: string): Promise<boolean>;
}
